import { useAuthorizedQuery } from "../api/authorized-query";
import { seedgreenAPI } from "../api/axios";
import { PlantingDTO } from "../dtos/PlantingDTO";
import { STALE_TIME_MEDIUM } from "../query/query.constants";
import { useQuery, useQueryClient, useMutation } from "@tanstack/vue-query";
import { computed } from "vue";
const PLANTING_STALE_TIME = STALE_TIME_MEDIUM;
export function usePlantings(options = {}) {
    const query = useQuery({
        enabled: useAuthorizedQuery(),
        queryKey: [
            "plantings",
            options.finalDateAfter?.value,
            options.wetDateBefore?.value,
            options.wetDateAfter?.value,
        ],
        queryFn: async () => (await seedgreenAPI.get("v2/Planting", {
            params: {
                finalDateAfter: options.finalDateAfter?.value,
                wetDateBefore: options.wetDateBefore?.value,
                wetDateAfter: options.wetDateAfter?.value,
            },
        })).data,
        staleTime: PLANTING_STALE_TIME,
        select: (data) => {
            try {
                return (data?.map((planting) => new PlantingDTO(planting)) ?? []);
            }
            catch (error) {
                console.error("query/plantings select ERROR", error);
            }
        },
    });
    return {
        plantings: query.data,
        ...query,
    };
}
export function usePlanting(id) {
    const queryClient = useQueryClient();
    const query = useQuery({
        enabled: useAuthorizedQuery(),
        queryKey: ["planting", id],
        queryFn: async () => {
            if (!id.value)
                return null;
            return (await seedgreenAPI.get(`v2/Planting/${id.value}`)).data;
        },
        staleTime: PLANTING_STALE_TIME,
        select: (data) => {
            if (!data)
                return null;
            try {
                return new PlantingDTO(data);
            }
            catch (error) {
                console.error("query/planting select ERROR", error);
            }
        },
        initialData: () => {
            // Search the cache for this planting
            const queries = queryClient.getQueriesData({
                queryKey: ["plantings"],
            });
            for (const [_, value] of queries) {
                if (Array.isArray(value)) {
                    for (const p of value) {
                        if (p.id === id.value) {
                            return p;
                        }
                    }
                }
            }
        },
    });
    return {
        planting: query.data,
        ...query,
    };
}
export function usePlantingsForLot(lotId) {
    const queryClient = useQueryClient();
    const query = useQuery({
        enabled: useAuthorizedQuery(),
        queryKey: ["plantings", { lotId: lotId }],
        queryFn: async () => (await seedgreenAPI.get("v2/Planting", {
            params: {
                lotId: lotId.value,
            },
        })).data,
        staleTime: PLANTING_STALE_TIME,
        select: (data) => {
            try {
                return (data?.map((planting) => new PlantingDTO(planting)) ?? []);
            }
            catch (error) {
                console.error("query/plantings select ERROR", error);
            }
        },
        placeholderData: () => {
            // Search the cache for plantings we already have
            const queries = queryClient.getQueriesData({
                queryKey: ["plantings"],
            });
            const idsFound = new Set();
            const result = [];
            for (const [_, value] of queries) {
                if (Array.isArray(value)) {
                    for (const p of value) {
                        if (p.id &&
                            !idsFound.has(p.id) &&
                            p.lotIds.includes(lotId.value)) {
                            idsFound.add(p.id);
                            result.push(p);
                        }
                    }
                }
            }
            return result.length ? result : undefined;
        },
    });
    return {
        plantingsForLot: query.data,
        ...query,
    };
}
export function usePlantingsByIds(plantingIds) {
    const isAuthorized = useAuthorizedQuery();
    const query = useQuery({
        enabled: computed(() => isAuthorized.value && plantingIds.value.length > 0),
        queryKey: computed(() => {
            // Create a stable, normalized key: deduplicated and sorted
            const normalizedIds = [...new Set(plantingIds.value)].sort((a, b) => a - b);
            return ["plantings", "byIds", normalizedIds];
        }),
        queryFn: async () => {
            const uniqueIds = [...new Set(plantingIds.value)].sort();
            return (await seedgreenAPI.post("v2/Planting/ByIds", uniqueIds))
                .data;
        },
        staleTime: PLANTING_STALE_TIME,
        select: (data) => {
            try {
                return (data?.map((planting) => new PlantingDTO(planting)) ?? []);
            }
            catch (error) {
                console.error("query/plantings by ids select ERROR", error);
            }
        },
    });
    return {
        plantings: query.data,
        ...query,
    };
}
export function usePatchPlanting() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async ({ id, patches }) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            await seedgreenAPI.patch(`v2/Planting/${id}`, patches);
        },
        onSuccess: (_, { id }) => {
            // Invalidate the specific planting
            queryClient.invalidateQueries({ queryKey: ["planting", id] });
            // Invalidate all planting lists (catches all variants)
            queryClient.invalidateQueries({ queryKey: ["plantings"] });
        },
    });
    return {
        patchPlanting: mutateAsync,
        isPending,
        error,
    };
}
export function useCreateBatchPlantings() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async (plantings) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.post("v2/Planting/Batch", {
                plantings,
            });
            return response.data;
        },
        onSuccess: () => {
            // Invalidate all planting lists to refresh data
            queryClient.invalidateQueries({ queryKey: ["plantings"] });
        },
    });
    return {
        createPlantings: mutateAsync,
        isPending,
        error,
    };
}
